import styled from "@emotion/styled";
import { fadeIn, duration } from "@atlas-int/tokens";
import { format, scaleLinear } from "d3";
import { ScaleLinear } from "d3";
import { LocationDetailLevel } from "@atlas-int/data/types";
import { formatTotalValue, ColorBy, UIView } from "@atlas-int/data";
import { memo, useEffect, useRef, useState } from "react";
import type { HierarchyRectangularNode } from "d3-hierarchy";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";

const labelCellPadding = 8;

const Label = styled.div`
  pointer-events: none;
  position: fixed;
  overflow: hidden;
  padding: ${labelCellPadding}px;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;

  opacity: 0;

  animation-name: ${fadeIn};
  animation-duration: ${duration.fast};
  animation-timing-function: linear;
  animation-delay: ${duration.slow};
  animation-iteration-count: 1;
  animation-fill-mode: forwards;

  & * {
    box-sizing: border-box;
  }
`;

const TextContent = styled.p`
  margin: 0px;
  padding: 0px;
  padding-right: ${labelCellPadding}px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;

  line-height: 120%;
  display: inline-block;

  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
`;

const HiddenTextContent = styled(TextContent)`
  text-overflow: unset;
  overflow: unset;
  white-space: unset;
  width: unset;
  visibility: hidden;
`;

const PercentOfTotalShown = styled.p<{ $fontSize: number }>`
  position: absolute;
  top: calc(${({ $fontSize }) => $fontSize}rem + ${labelCellPadding}px);
  left: ${labelCellPadding}px;
  padding: 0px;
  margin: 0px;
  text-align: left;
  line-height: 120%;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
`;

const fractionFontSizeScale: ScaleLinear<number, number> = scaleLinear()
  .domain([0, 0.1])
  .range([0.5, 2])
  .clamp(true);

const areaFontSizeScale: ScaleLinear<number, number> = scaleLinear()
  .domain([0, 40000])
  .range([0.4, 2.5])
  .clamp(true);

const cellWidthToLabelWidthRatioThreshold = (cellWidth: number) => {
  return cellWidth - 2 * labelCellPadding;
};

const cellWidthToPercentageLabelWidthRatioThreshold = (input: number) => {
  return input - 2 * labelCellPadding;
};

const cellHeightToLabelHeightRatioThreshold = (input: number) => {
  return input - 2 * labelCellPadding;
};

const ellipsisCharacter = "…";

const mustShowLabelThreshold = 0.05;
const doNotShowLabelThreshold = 0.001;
const minimumCharactersToDisplay = 3;

interface TreemapCellLabelProps {
  leaf: HierarchyRectangularNode<EnrichedTradeDataItem>;
  totalSum: number | undefined;
  nonServices?: boolean;
  servicesWidth?: number;
  view: UIView;
  colorBy: ColorBy;
  locationDetailLevel: string;
}

const TreemapCellLabel = ({
  leaf,
  totalSum,
  nonServices,
  servicesWidth,
  view,
  colorBy,
  locationDetailLevel: locationLevel,
}: TreemapCellLabelProps) => {

  const textLabelContentRef = useRef<HTMLDivElement | null>(null);
  const valueLabelContentRef = useRef<HTMLDivElement | null>(null);

  const [showTextLabel, setShowTextLabel] = useState<boolean>(false);
  const [showValueLabel, setShowValueLabel] = useState<boolean>(false);

  const fractionOfTotalValue = leaf.value! / totalSum!;

  const cellWidth = leaf.x1 - leaf.x0;
  const cellHeight = leaf.y1 - leaf.y0;

  const cellArea = cellWidth * cellHeight;
  const cellFontSize = Math.min(
    fractionFontSizeScale(fractionOfTotalValue),
    areaFontSizeScale(cellArea),
  );

  const cellStyle = {
    width: `${cellWidth}px`,
    height: `${cellHeight}px`,
    fontSize: `${cellFontSize}rem`,
    color: "#ffffff",
  };

  let labelValueToUse: string = "";
  if (view === UIView.Markets) {
    if (locationLevel === LocationDetailLevel.country) {
      labelValueToUse = leaf.data.nameEn || "";
    } else if (locationLevel === LocationDetailLevel.subregion) {
      labelValueToUse = leaf.data.hybrid_level_2?.nameEn || "";
    } else if (locationLevel === LocationDetailLevel.region) {
      labelValueToUse = leaf.data.hybrid_level_1?.nameEn || "";
    }
  } else if (view === UIView.Products) {
    labelValueToUse = leaf.data.nameEn || "";
  }

  let cellValueLabelString;
  if (colorBy === ColorBy.Sector) {
    cellValueLabelString = `$${formatTotalValue(leaf.value)}`;
  } else if (colorBy === ColorBy.Complexity) {
    cellValueLabelString = format(".3f")(leaf.data.pci as number);
  }

  // Only create hidden labels if we have a label value
  let hiddenTextContentLabel = labelValueToUse ? (
    <HiddenTextContent
      ref={textLabelContentRef}
    >{`${labelValueToUse.slice(0, minimumCharactersToDisplay)}${ellipsisCharacter}`}</HiddenTextContent>
  ) : null;

  let hiddenCellValueLabel = (
    <HiddenTextContent ref={valueLabelContentRef}>
      {cellValueLabelString}
    </HiddenTextContent>
  );

  let textContentLabel = labelValueToUse ? <TextContent>{labelValueToUse}</TextContent> : null;
  let cellValueLabel = (
    <PercentOfTotalShown $fontSize={cellFontSize}>
      {cellValueLabelString}
    </PercentOfTotalShown>
  );

  const xOffset = nonServices ? leaf.x0 + (servicesWidth ?? 0) : leaf.x0;

  useEffect(() => {
    if (textLabelContentRef.current && labelValueToUse) {
      const { width: textLabelWidth, height: textLabelHeight } =
        textLabelContentRef.current.getBoundingClientRect();

      if (fractionOfTotalValue >= mustShowLabelThreshold) {
        setShowTextLabel(true);
      } else if (fractionOfTotalValue < doNotShowLabelThreshold) {
        setShowTextLabel(false);
      } else if (
        textLabelWidth > 0 &&
        textLabelWidth <= cellWidthToLabelWidthRatioThreshold(cellWidth) &&
        textLabelHeight > 0 &&
        textLabelHeight <= cellHeightToLabelHeightRatioThreshold(cellHeight)
      ) {
        setShowTextLabel(true);
      } else {
        setShowTextLabel(false);
      }
    } else {
      setShowTextLabel(false);
    }
  }, [cellWidth, cellHeight, fractionOfTotalValue, labelValueToUse]);

  useEffect(() => {
    if (valueLabelContentRef.current) {
      const { width: valueLabelWidth, height: valueLabelHeight } =
        valueLabelContentRef.current.getBoundingClientRect();
      const combinedHeight = 2 * valueLabelHeight;
      if (
        valueLabelWidth > 0 &&
        valueLabelWidth <=
          cellWidthToPercentageLabelWidthRatioThreshold(cellWidth) &&
        valueLabelHeight > 0 &&
        combinedHeight <= cellHeightToLabelHeightRatioThreshold(cellHeight)
      ) {
        setShowValueLabel(true);
      } else {
        setShowValueLabel(false);
      }
    } else {
      setShowValueLabel(false);
    }
  }, [cellWidth, cellHeight, fractionOfTotalValue, showTextLabel]);

  const hasVisibleLabel = showTextLabel || showValueLabel;

  return (
    <foreignObject
      x={xOffset}
      y={leaf.y0}
      width={cellWidth}
      height={cellHeight}
      pointerEvents="none"
    >
      <Label style={cellStyle}>
        <div style={{ position: "absolute", visibility: "hidden", pointerEvents: "none" }}>
          {hiddenTextContentLabel}
          {hiddenCellValueLabel}
        </div>
        {hasVisibleLabel && showTextLabel && textContentLabel}
        {hasVisibleLabel && showTextLabel && showValueLabel && cellValueLabel}
      </Label>
    </foreignObject>
  )
};

export default memo(TreemapCellLabel);
